import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Save, Send, CheckCircle2, XCircle, MessageCircle, Plus, Trash2, Zap, User, CalendarRange, DoorClosed, RefreshCw, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import OwnerLayout from "@/components/owner/OwnerLayout";
import apiClient, { formatApiErrorDetail } from "@/lib/api";
import { BILLING_STATUS_LABELS, BILLING_STATUS_STYLES, DELIVERY_LABELS, formatRupiah, formatDateID, UNIT_OPTIONS, ITEM_TYPE_LABELS, computeItemAmount } from "@/lib/billingMeta";

const PERIOD_OPTS = [
  { v: "daily", l: "Harian" },
  { v: "monthly", l: "Bulanan" },
  { v: "yearly", l: "Tahunan" },
];

// Edit tenant dates/data directly from billing, then re-sync the draft.
function EditTenantModal({ billing, onClose, onSynced }) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [t, setT] = useState({ tenant_name: "", phone: "", start_date: "", end_date: "", rental_period: "monthly", notes: "" });

  useEffect(() => {
    apiClient.get(`/rooms/${billing.room_id}/tenants`).then((r) => {
      const found = (r.data || []).find((x) => x.id === billing.tenant_id);
      if (found) setT({
        tenant_name: found.tenant_name || "", phone: found.phone || "",
        start_date: found.start_date || "", end_date: found.end_date || "",
        rental_period: found.rental_period || "monthly", notes: found.notes || "",
      });
    }).catch(() => {}).finally(() => setLoading(false));
  }, [billing.room_id, billing.tenant_id]);

  const save = async () => {
    if (!t.tenant_name.trim()) { toast.error("Nama penghuni wajib diisi."); return; }
    if (!t.start_date) { toast.error("Tanggal mulai sewa wajib diisi."); return; }
    setBusy(true);
    try {
      await apiClient.put(`/tenants/${billing.tenant_id}`, {
        tenant_name: t.tenant_name.trim(), phone: t.phone.trim() || null,
        start_date: t.start_date, end_date: t.end_date || null,
        rental_period: t.rental_period, notes: t.notes.trim() || null,
      });
      await apiClient.post(`/billing/${billing.id}/sync-tenant`);
      toast.success("Data penghuni diperbarui & tagihan disinkronkan.");
      onSynced?.();
      onClose?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-black/40 p-4" data-testid="edit-tenant-modal" onClick={onClose}>
      <div className="bg-card rounded-2xl border border-border w-full max-w-md max-h-[85vh] overflow-y-auto" onClick={(ev) => ev.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-border">
          <h3 className="font-heading text-lg font-semibold flex items-center gap-2"><Pencil size={16} className="text-primary" /> Edit Data Penghuni</h3>
          <button onClick={onClose} data-testid="edit-tenant-close" className="p-1.5 rounded-lg hover:bg-secondary/60"><X size={18} /></button>
        </div>
        {loading ? <div className="py-10 grid place-items-center"><Loader2 className="animate-spin text-primary" /></div> : (
          <div className="p-5 space-y-3">
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Nama</label>
              <input value={t.tenant_name} onChange={(ev) => setT({ ...t, tenant_name: ev.target.value })} data-testid="edit-tenant-name"
                className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">No. HP</label>
              <input value={t.phone} onChange={(ev) => setT({ ...t, phone: ev.target.value })} data-testid="edit-tenant-phone"
                className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-muted-foreground mb-1">Tanggal Mulai Sewa</label>
                <input type="date" value={t.start_date} onChange={(ev) => setT({ ...t, start_date: ev.target.value })} data-testid="edit-tenant-start"
                  className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
              </div>
              <div>
                <label className="block text-xs text-muted-foreground mb-1">Tanggal Keluar (opsional)</label>
                <input type="date" value={t.end_date} onChange={(ev) => setT({ ...t, end_date: ev.target.value })} data-testid="edit-tenant-end"
                  className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">Kosongkan Tanggal Keluar jika penghuni masih tinggal. Isi hanya jika sudah/akan pindah.</p>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Periode Sewa</label>
              <select value={t.rental_period} onChange={(ev) => setT({ ...t, rental_period: ev.target.value })} data-testid="edit-tenant-period"
                className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30">
                {PERIOD_OPTS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
            </div>
            <button onClick={save} disabled={busy} data-testid="edit-tenant-save"
              className="w-full py-2.5 rounded-full bg-primary text-primary-foreground font-semibold hover:scale-[1.02] active:scale-95 transition-transform disabled:opacity-60 inline-flex items-center justify-center gap-2">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Simpan & Sinkron
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function LineRow({ label, value, strong }) {
  return (
    <div className="flex justify-between gap-4 py-1.5 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className={strong ? "font-semibold text-foreground" : "font-medium"}>{value}</span>
    </div>
  );
}

export default function OwnerBillingDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [b, setB] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [editTenant, setEditTenant] = useState(false);
  // draft edit state
  const [form, setForm] = useState(null);

  const load = () => {
    setLoading(true);
    apiClient.get(`/billing/${id}`).then((r) => {
      setB(r.data);
      const e = r.data.electricity || {};
      setForm({
        room_price_snapshot: r.data.room_price_snapshot ?? "",
        check_in: r.data.check_in || "",
        check_out: r.data.check_out || "",
        meter_start: e.meter_start ?? "",
        meter_end: e.meter_end ?? "",
        price_per_kwh: e.price_per_kwh ?? "",
        extra_items: (r.data.extra_items || []).map((x) => ({ ...x })),
        due_date: r.data.due_date || "",
        note: r.data.note || "",
      });
    }).catch(() => setB(null)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const isDraft = b?.status === "draft";

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const addItem = () => setForm((f) => ({ ...f, extra_items: [...f.extra_items, { name: "", type: "fixed", unit: "", price_per_unit: "", meter_start: "", meter_end: "", quantity: "", amount: 0, note: "" }] }));
  const rmItem = (i) => setForm((f) => ({ ...f, extra_items: f.extra_items.filter((_, idx) => idx !== i) }));
  const setItem = (i, k, v) => setForm((f) => ({ ...f, extra_items: f.extra_items.map((x, idx) => idx === i ? { ...x, [k]: v } : x) }));

  const save = async () => {
    for (const it of form.extra_items) {
      if (!String(it.name).trim()) { toast.error("Nama komponen biaya wajib diisi."); return; }
    }
    setBusy("save");
    try {
      const payload = {
        room_price_snapshot: form.room_price_snapshot === "" ? null : Number(form.room_price_snapshot),
        meter_start: form.meter_start === "" ? null : Number(form.meter_start),
        meter_end: form.meter_end === "" ? null : Number(form.meter_end),
        price_per_kwh: form.price_per_kwh === "" ? null : Number(form.price_per_kwh),
        extra_items: form.extra_items.map((x) => {
          const type = x.type || "fixed";
          return {
            name: String(x.name).trim(),
            type,
            unit: type !== "fixed" ? (x.unit || null) : null,
            price_per_unit: type !== "fixed" ? Number(x.price_per_unit || 0) : null,
            meter_start: type === "metered" ? (x.meter_start === "" || x.meter_start == null ? null : Number(x.meter_start)) : null,
            meter_end: type === "metered" ? (x.meter_end === "" || x.meter_end == null ? null : Number(x.meter_end)) : null,
            quantity: type === "quantity" ? (x.quantity === "" || x.quantity == null ? null : Number(x.quantity)) : null,
            amount: type === "fixed" ? Number(x.amount || 0) : 0,
            note: x.note || null,
          };
        }),
        due_date: form.due_date || null,
        note: form.note || null,
      };
      const { data } = await apiClient.put(`/billing/${id}`, payload);
      setB(data);
      toast.success("Perubahan disimpan.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally { setBusy(""); }
  };

  const act = async (path, label, method = "post", body) => {
    setBusy(path);
    try {
      const { data } = await apiClient[method](`/billing/${id}/${path}`, body);
      setB(data);
      toast.success(label);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally { setBusy(""); }
  };

  const sendWhatsApp = async () => {
    setBusy("wa");
    try {
      const { data } = await apiClient.get(`/billing/${id}/message`);
      if (data.wa_link) {
        const url = `${data.wa_link}?text=${encodeURIComponent(data.message)}`;
        window.open(url, "_blank", "noopener,noreferrer");
      } else {
        toast.warning("Nomor HP penghuni tidak tersedia. Menyalin teks invoice.");
        navigator.clipboard?.writeText(data.message);
      }
      await apiClient.post(`/billing/${id}/delivery`, { delivery_status: "sent" });
      toast.success("Ditandai terkirim.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally { setBusy(""); }
  };

  if (loading) return <OwnerLayout><div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-primary" size={28} /></div></OwnerLayout>;
  if (!b) return <OwnerLayout><div className="py-20 text-center text-muted-foreground">Tagihan tidak ditemukan.</div></OwnerLayout>;

  const e = b.electricity || {};
  const _bf = b.billed_from || b.check_in;
  const _bt = b.billed_to;
  const billedRange = (_bf && _bt)
    ? `${formatDateID(_bf)} – ${formatDateID(_bt)} (${b.billed_days} hari)`
    : `${b.billed_days} dari ${b.days_in_month} hari`;

  return (
    <OwnerLayout>
      <button onClick={() => navigate("/owner/billing")} data-testid="billing-detail-back"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft size={16} /> Kembali ke daftar
      </button>

      <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
        <div>
          <h1 className="font-heading text-2xl font-bold">Invoice {b.invoice_no}</h1>
          <p className="text-sm text-muted-foreground">Periode {b.period_label}</p>
        </div>
        <span className={`text-sm font-semibold px-3 py-1 rounded-full border ${BILLING_STATUS_STYLES[b.status] || ""}`} data-testid="billing-detail-status">
          {BILLING_STATUS_LABELS[b.status] || b.status}
        </span>
      </div>

      <div className="grid lg:grid-cols-3 gap-5" data-testid="billing-detail">
        {/* Left: invoice */}
        <div className="lg:col-span-2 space-y-5">
          {/* Penghuni + periode */}
          <div className="bg-card border border-border rounded-2xl p-5">
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 mb-2"><User size={13} /> Penghuni</p>
                <p className="font-semibold">{b.tenant_name || "-"}</p>
                <p className="text-muted-foreground">{b.tenant_phone || "-"}</p>
                <p className="text-muted-foreground inline-flex items-center gap-1.5 mt-1"><DoorClosed size={13} /> {b.property_name} · Kamar {b.room_number}</p>
                {isDraft && (
                  <button onClick={() => setEditTenant(true)} data-testid="billing-edit-tenant-open"
                    className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border text-xs font-semibold hover:bg-secondary/60 transition-colors">
                    <Pencil size={13} /> Edit Data Penghuni
                  </button>
                )}
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 mb-2"><CalendarRange size={13} /> Periode Ditagih</p>
                <LineRow label="Rentang dihitung" value={billedRange} />
                <LineRow label="Jumlah hari" value={`${b.billed_days} dari ${b.days_in_month} hari${b.is_prorated ? " (prorata)" : ""}`} />
                <LineRow label="Mulai sewa" value={formatDateID(b.check_in)} />
                <LineRow label="Status" value={b.check_out ? `Keluar ${formatDateID(b.check_out)}` : "Masih aktif"} />
              </div>
            </div>
          </div>

          {/* Kamar */}
          <div className="bg-card border border-border rounded-2xl p-5">
            <h3 className="font-heading font-semibold mb-3">Sewa Kamar</h3>
            {isDraft ? (
              <>
                <div className="flex flex-col gap-2 rounded-xl bg-primary/5 border border-primary/15 px-4 py-3 text-xs text-muted-foreground mb-3" data-testid="billing-checkin-note">
                  <span>Check-in & check-out mengikuti data penghuni dan tidak diubah di sini. Untuk mengoreksi, edit data penghuni lalu tagihan otomatis disinkronkan.</span>
                  <button onClick={() => setEditTenant(true)} data-testid="billing-checkin-note-edit"
                    className="self-start inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary text-primary-foreground text-xs font-semibold hover:scale-[1.03] active:scale-95 transition-transform">
                    <Pencil size={13} /> Edit Data Penghuni
                  </button>
                </div>
                <div className="mb-3">
                  <label className="block text-xs text-muted-foreground mb-1">Harga kamar / bulan</label>
                  <input type="number" value={form.room_price_snapshot} onChange={(ev) => setF("room_price_snapshot", ev.target.value)} data-testid="billing-edit-price"
                    className="w-full sm:w-1/2 rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
                </div>
              </>
            ) : null}
            <LineRow label="Harga kamar normal / bulan" value={formatRupiah(b.room_price_snapshot)} />
            <LineRow label={`Ditagihkan (${b.billed_days}/${b.days_in_month} hari${b.is_prorated ? ", prorata" : ""})`} value={formatRupiah(b.room_charge)} strong />
          </div>

          {/* Listrik */}
          <div className="bg-card border border-border rounded-2xl p-5">
            <h3 className="font-heading font-semibold mb-3 flex items-center gap-2"><Zap size={16} className="text-amber-500" /> Listrik</h3>
            {isDraft ? (
              <div className="grid grid-cols-3 gap-3 mb-3">
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">Meteran awal</label>
                  <input type="number" value={form.meter_start} onChange={(ev) => setF("meter_start", ev.target.value)} data-testid="billing-edit-meter-start"
                    className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">Meteran akhir</label>
                  <input type="number" value={form.meter_end} onChange={(ev) => setF("meter_end", ev.target.value)} data-testid="billing-edit-meter-end"
                    className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">Harga / kWh</label>
                  <input type="number" value={form.price_per_kwh} onChange={(ev) => setF("price_per_kwh", ev.target.value)} data-testid="billing-edit-ppk"
                    className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
                </div>
              </div>
            ) : null}
            <LineRow label="Meteran awal" value={`${e.meter_start ?? "-"} kWh`} />
            <LineRow label="Meteran akhir" value={`${e.meter_end ?? "-"} kWh`} />
            <LineRow label="Pemakaian" value={`${e.usage_kwh ?? 0} kWh`} />
            <LineRow label="Harga / kWh" value={formatRupiah(e.price_per_kwh)} />
            <LineRow label="Total listrik" value={formatRupiah(e.total)} strong />
          </div>

          {/* Biaya tambahan */}
          <div className="bg-card border border-border rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading font-semibold">Biaya Lain</h3>
              {isDraft && (
                <div className="flex gap-2">
                  <button onClick={() => act("apply-defaults", "Komponen template ditambahkan.")} disabled={busy === "apply-defaults"} data-testid="billing-apply-defaults"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border text-xs font-semibold hover:bg-secondary/60 disabled:opacity-60">
                    {busy === "apply-defaults" ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Dari Template
                  </button>
                  <button onClick={addItem} data-testid="billing-edit-add-item" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border text-xs font-semibold hover:bg-secondary/60"><Plus size={14} /> Tambah</button>
                </div>
              )}
            </div>
            {isDraft ? (
              form.extra_items.length === 0 ? <p className="text-sm text-muted-foreground">Belum ada komponen biaya.</p> :
              <div className="space-y-2">
                {form.extra_items.map((it, i) => {
                  const type = it.type || "fixed";
                  return (
                  <div key={i} className="rounded-xl border border-border p-3 space-y-2" data-testid={`billing-edit-item-${i}`}>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <input value={it.name} onChange={(ev) => setItem(i, "name", ev.target.value)} placeholder="Nama (mis. Air PAM)" data-testid={`billing-edit-item-name-${i}`}
                        className="flex-1 rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
                      <select value={type} onChange={(ev) => setItem(i, "type", ev.target.value)} data-testid={`billing-edit-item-type-${i}`}
                        className="sm:w-52 rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30">
                        {Object.entries(ITEM_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                      <button onClick={() => rmItem(i)} data-testid={`billing-edit-item-remove-${i}`} className="grid place-items-center w-10 h-10 shrink-0 rounded-xl border border-rose-200 text-rose-600 hover:bg-rose-50"><Trash2 size={16} /></button>
                    </div>
                    {type === "fixed" && (
                      <div>
                        <label className="block text-xs text-muted-foreground mb-1">Nominal (Rp)</label>
                        <input type="number" value={it.amount} onChange={(ev) => setItem(i, "amount", ev.target.value)} placeholder="mis. 50000" data-testid={`billing-edit-item-amount-${i}`}
                          className="w-full sm:w-1/2 rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
                      </div>
                    )}
                    {type === "metered" && (
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        <div>
                          <label className="block text-xs text-muted-foreground mb-1">Satuan</label>
                          <input value={it.unit || ""} onChange={(ev) => setItem(i, "unit", ev.target.value)} list="unit-options" placeholder="mis. m³" data-testid={`billing-edit-item-unit-${i}`}
                            className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
                        </div>
                        <div>
                          <label className="block text-xs text-muted-foreground mb-1">Meteran Awal</label>
                          <input type="number" value={it.meter_start} onChange={(ev) => setItem(i, "meter_start", ev.target.value)} placeholder="mis. 155" data-testid={`billing-edit-item-mstart-${i}`}
                            className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
                        </div>
                        <div>
                          <label className="block text-xs text-muted-foreground mb-1">Meteran Akhir</label>
                          <input type="number" value={it.meter_end} onChange={(ev) => setItem(i, "meter_end", ev.target.value)} placeholder="mis. 200" data-testid={`billing-edit-item-mend-${i}`}
                            className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
                        </div>
                        <div>
                          <label className="block text-xs text-muted-foreground mb-1">Harga / Satuan</label>
                          <input type="number" value={it.price_per_unit} onChange={(ev) => setItem(i, "price_per_unit", ev.target.value)} placeholder="mis. 5000" data-testid={`billing-edit-item-price-${i}`}
                            className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
                        </div>
                      </div>
                    )}
                    {type === "quantity" && (
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <label className="block text-xs text-muted-foreground mb-1">Satuan</label>
                          <input value={it.unit || ""} onChange={(ev) => setItem(i, "unit", ev.target.value)} list="unit-options" placeholder="mis. galon" data-testid={`billing-edit-item-unit-${i}`}
                            className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
                        </div>
                        <div>
                          <label className="block text-xs text-muted-foreground mb-1">Jumlah</label>
                          <input type="number" value={it.quantity} onChange={(ev) => setItem(i, "quantity", ev.target.value)} placeholder="mis. 3" data-testid={`billing-edit-item-qty-${i}`}
                            className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
                        </div>
                        <div>
                          <label className="block text-xs text-muted-foreground mb-1">Harga / Satuan</label>
                          <input type="number" value={it.price_per_unit} onChange={(ev) => setItem(i, "price_per_unit", ev.target.value)} placeholder="mis. 20000" data-testid={`billing-edit-item-price-${i}`}
                            className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
                        </div>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <input value={it.note || ""} onChange={(ev) => setItem(i, "note", ev.target.value)} placeholder="Catatan (opsional)"
                        className="flex-1 rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
                      <span className="text-sm font-semibold whitespace-nowrap" data-testid={`billing-edit-item-total-${i}`}>{formatRupiah(computeItemAmount(it))}</span>
                    </div>
                  </div>
                  );
                })}
                <datalist id="unit-options">{UNIT_OPTIONS.map((u) => <option key={u} value={u} />)}</datalist>
              </div>
            ) : (
              (b.extra_items || []).length === 0 ? <p className="text-sm text-muted-foreground">Tidak ada biaya tambahan.</p> :
              (b.extra_items || []).map((it, i) => {
                let sub = "";
                if (it.type === "metered" && it.meter_start != null && it.meter_end != null) sub = ` · ${Math.round((it.meter_end - it.meter_start) * 100) / 100} ${it.unit || ""} × ${formatRupiah(it.price_per_unit)}`;
                else if (it.type === "quantity" && it.quantity != null) sub = ` · ${it.quantity} ${it.unit || ""} × ${formatRupiah(it.price_per_unit)}`;
                const label = (it.note ? `${it.name} (${it.note})` : it.name) + sub;
                return <LineRow key={i} label={label} value={formatRupiah(it.amount)} />;
              })
            )}
          </div>
        </div>

        {/* Right: totals + actions */}
        <div className="space-y-5">
          <div className="bg-card border border-border rounded-2xl p-5" data-testid="billing-total-card">
            <h3 className="font-heading font-semibold mb-3">Ringkasan</h3>
            <LineRow label="Subtotal kamar" value={formatRupiah(b.subtotal_room)} />
            <LineRow label="Total listrik" value={formatRupiah(b.subtotal_electricity)} />
            <LineRow label="Biaya lain" value={formatRupiah(b.subtotal_extra)} />
            <div className="border-t border-border mt-2 pt-2">
              <div className="flex justify-between items-center">
                <span className="font-semibold">Grand Total</span>
                <span className="font-heading text-xl font-bold text-primary" data-testid="billing-grand-total">{formatRupiah(b.total)}</span>
              </div>
            </div>
            {isDraft && (
              <div className="mt-4 space-y-3">
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">Jatuh tempo</label>
                  <input type="date" value={form.due_date} onChange={(ev) => setF("due_date", ev.target.value)} data-testid="billing-edit-due"
                    className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">Catatan</label>
                  <textarea value={form.note} onChange={(ev) => setF("note", ev.target.value)} rows={2} data-testid="billing-edit-note"
                    className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
                </div>
              </div>
            )}
            {!isDraft && b.due_date && <p className="mt-3 text-sm text-muted-foreground">Jatuh tempo: <span className="font-medium text-foreground">{formatDateID(b.due_date)}</span></p>}
            <p className="mt-3 text-xs text-muted-foreground">Status kirim: {DELIVERY_LABELS[b.delivery_status] || b.delivery_status}</p>
          </div>

          {/* Actions */}
          {(isDraft || b.status === "published" || b.status === "sent" || b.status === "overdue") && (
          <div className="bg-card border border-border rounded-2xl p-5 space-y-2" data-testid="billing-actions">
            {isDraft && (
              <>
                <button onClick={() => act("sync-tenant", "Data check-in/out disinkronkan dari penghuni.")} disabled={busy === "sync-tenant"} data-testid="billing-sync-tenant"
                  className="w-full py-2.5 rounded-full border border-border font-semibold hover:bg-secondary/60 transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2">
                  {busy === "sync-tenant" ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />} Sinkron dari Penghuni
                </button>
                <button onClick={save} disabled={busy === "save"} data-testid="billing-save"
                  className="w-full py-2.5 rounded-full border border-border font-semibold hover:bg-secondary/60 transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2">
                  {busy === "save" ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Simpan Draft
                </button>
                <button onClick={() => act("publish", "Tagihan dipublish.")} disabled={busy === "publish"} data-testid="billing-publish"
                  className="w-full py-2.5 rounded-full bg-primary text-primary-foreground font-semibold hover:scale-[1.02] active:scale-95 transition-transform disabled:opacity-60 inline-flex items-center justify-center gap-2">
                  {busy === "publish" ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />} Publish Tagihan
                </button>
              </>
            )}
            {(b.status === "published" || b.status === "sent" || b.status === "overdue") && (
              <>
                <button onClick={sendWhatsApp} disabled={busy === "wa"} data-testid="billing-send-wa"
                  className="w-full py-2.5 rounded-full bg-emerald-500 text-white font-semibold hover:bg-emerald-600 transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2">
                  {busy === "wa" ? <Loader2 size={16} className="animate-spin" /> : <MessageCircle size={16} />} Kirim via WhatsApp
                </button>
                <button onClick={() => act("mark-paid", "Tagihan ditandai lunas.")} disabled={busy === "mark-paid"} data-testid="billing-mark-paid"
                  className="w-full py-2.5 rounded-full border border-emerald-200 text-emerald-700 font-semibold hover:bg-emerald-50 transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2">
                  {busy === "mark-paid" ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Tandai Lunas
                </button>
              </>
            )}
            {b.status !== "paid" && b.status !== "cancelled" && (
              <button onClick={() => act("cancel", "Tagihan dibatalkan.")} disabled={busy === "cancel"} data-testid="billing-cancel"
                className="w-full py-2.5 rounded-full border border-rose-200 text-rose-600 font-semibold hover:bg-rose-50 transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2">
                {busy === "cancel" ? <Loader2 size={16} className="animate-spin" /> : <XCircle size={16} />} Batalkan
              </button>
            )}
          </div>
          )}
        </div>
      </div>
      {editTenant && <EditTenantModal billing={b} onClose={() => setEditTenant(false)} onSynced={load} />}
    </OwnerLayout>
  );
}
